import axios from 'axios';
import { urlArray } from './urlArrayNotForEncryption';
import RefreshTokenController from '../../src/controller/refreshTokenController';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_BACKOFFICE_URL,
  headers: {
    'Content-type': 'application/json',
  },
  withCredentials: true, // Include credentials (cookies) in requests
});

// Create a place to inject external dependencies (like auth updater)
let authHelper = {
  setUserFromToken: null,
  redirectToLogin: () => (window.location.href = '/login'),
};

export const injectAuthHelper = (helper) => {
  authHelper = { ...authHelper, ...helper };
};

// Add a request interceptor to encrypt the data before sending the request
apiClient.interceptors.request.use(
  async (config) => {
    try {
      // Skip encryption for retried requests
      if (config.__skipEncryption) {
        return config;
      }
      if (config?.method === 'get' || config?.method === 'delete') {
        if (config.url && !urlArray.includes(config.url)) {
          // Extract the last part of the URL path and pass it for encryption
          const splitURLParts = config.url.split('/');
          const splitURLLastPart = splitURLParts[splitURLParts.length - 1];
          const encryptedURLData = splitURLLastPart;
          const encodedEncryptedURLData = encodeURIComponent(encryptedURLData);
          // Save the other URL parts in a variable
          const splitURLRemainingParts = splitURLParts.slice(0, splitURLParts.length - 1);
          const urlToPass = splitURLRemainingParts.join('/') + '/' + encodedEncryptedURLData;
          config.url = urlToPass;
        }
      }

      return config;
    } catch (error) {
      // Handle the error gracefully, such as logging it or notifying the user
      return Promise.reject(error);
    }
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Add a response interceptor to decrypt the data after receiving the response
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 403 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const errorMessage = error.response?.data?.message;
        if (errorMessage === 'Access denied: insufficient permissions') {
          window.location.href = '/notFound';
          return; // explicitly return after redirect
        }

        const controller = new RefreshTokenController();
        const newAccessTokenData = await controller.Refresh(sessionStorage.getItem('user'));
        const newAccessToken = newAccessTokenData?.data?.accessToken;
        // Save new token and update headers
        sessionStorage.setItem('user', newAccessToken);
        authHelper.setUserFromToken(newAccessToken);

        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
        originalRequest.__skipEncryption = true;

        // Retry original request with refreshed token
        return apiClient(originalRequest);
      } catch (refreshError) {
        sessionStorage.clear();
        authHelper.redirectToLogin(); // fallback redirect
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export default apiClient;
